import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, ArrowLeft, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { intercomAction } from '@/lib/api';

type IntercomStep = 'residents' | 'name' | 'waiting' | 'approved' | 'denied' | 'expired';

interface Resident {
  assignment_id: string;
  display_name: string;
}

interface IntercomFlowProps {
  deviceCode: string;
  organizationId: string;
  orgName?: string;
  orgLogoUrl?: string;
  onBack: () => void;
}

const POLL_INTERVAL_MS = 3000;

export function IntercomFlow({
  deviceCode,
  organizationId,
  orgName,
  orgLogoUrl,
  onBack,
}: IntercomFlowProps) {
  const [step, setStep] = useState<IntercomStep>('residents');
  const [residents, setResidents] = useState<Resident[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [visitorName, setVisitorName] = useState('');
  const [requestToken, setRequestToken] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [qrString, setQrString] = useState('');
  const [keyExpiresAt, setKeyExpiresAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch residents on mount
  useEffect(() => {
    async function fetchResidents() {
      setLoading(true);
      setError(null);
      const result = await intercomAction<{
        residents: Resident[];
        device_id: string;
        device_name: string;
      }>('intercom-residents', {
        device_code: deviceCode,
        organization_id: organizationId,
      });

      if (result.error || !result.data) {
        setError(result.error || 'Kunde inte hämta boende');
      } else {
        setResidents(result.data.residents);
        setDeviceId(result.data.device_id);
        setDeviceName(result.data.device_name || '');
      }
      setLoading(false);
    }

    fetchResidents();
  }, [deviceCode, organizationId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const pollStatus = useCallback(async () => {
    if (!requestToken) return;

    const result = await intercomAction<{
      status: string;
      qr_string?: string;
      key_expires_at?: string;
    }>('intercom-poll', { request_token: requestToken });

    if (result.data) {
      if (result.data.status === 'approved' && result.data.qr_string) {
        setQrString(result.data.qr_string);
        setKeyExpiresAt(result.data.key_expires_at || '');
        setStep('approved');
        if (pollRef.current) clearInterval(pollRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      } else if (result.data.status === 'denied') {
        setStep('denied');
        if (pollRef.current) clearInterval(pollRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      } else if (result.data.status === 'expired') {
        setStep('expired');
        if (pollRef.current) clearInterval(pollRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      }
    }
  }, [requestToken]);

  // Start polling when we have a request token
  useEffect(() => {
    if (step !== 'waiting' || !requestToken) return;

    pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
    // Also poll immediately
    pollStatus();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [step, requestToken, pollStatus]);

  // Countdown timer
  useEffect(() => {
    if (step !== 'waiting' || !expiresAt) return;

    const updateTimeLeft = () => {
      const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setStep('expired');
        if (pollRef.current) clearInterval(pollRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      }
    };

    updateTimeLeft();
    countdownRef.current = setInterval(updateTimeLeft, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [step, expiresAt]);

  const handleSelectResident = (resident: Resident) => {
    setSelectedResident(resident);
    setStep('name');
  };

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitorName.trim() || !selectedResident || !deviceId) return;

    setLoading(true);
    setError(null);

    const result = await intercomAction<{
      request_token: string;
      expires_at: string;
      resident_name: string;
    }>('intercom-request', {
      device_id: deviceId,
      assignment_id: selectedResident.assignment_id,
      visitor_name: visitorName.trim(),
    });

    setLoading(false);

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte skicka förfrågan');
      return;
    }

    setRequestToken(result.data.request_token);
    setExpiresAt(result.data.expires_at);
    setStep('waiting');
  };

  const handleReset = () => {
    setSelectedResident(null);
    setVisitorName('');
    setRequestToken('');
    setExpiresAt('');
    setQrString('');
    setKeyExpiresAt('');
    setError(null);
    setStep('residents');
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-br from-muted/30 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm space-y-6 relative z-10">
        {/* Header */}
        <div className="text-center space-y-3">
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt={orgName || 'Organisation'}
              className="mx-auto h-16 w-auto max-w-[180px] object-contain"
            />
          ) : (
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-card/80 backdrop-blur border border-border shadow-lg">
              <Bell className="h-8 w-8 text-foreground" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {orgName || 'Begär åtkomst'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {step === 'residents' && 'Välj vem du söker'}
              {step === 'name' && `Skicka förfrågan till ${selectedResident?.display_name}`}
              {step === 'waiting' && `Väntar på svar från ${selectedResident?.display_name}...`}
              {step === 'approved' && 'Åtkomst beviljad'}
              {step === 'denied' && 'Åtkomst nekad'}
              {step === 'expired' && 'Förfrågan gick ut'}
            </p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive animate-scale-in">
            {error}
          </div>
        )}

        {/* Step: Select resident */}
        {step === 'residents' && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6 space-y-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : residents.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Inga boende tillgängliga
                </p>
              ) : (
                residents.map((resident) => (
                  <Button
                    key={resident.assignment_id}
                    className="w-full justify-start"
                    size="lg"
                    variant="outline"
                    onClick={() => handleSelectResident(resident)}
                  >
                    <Bell className="h-4 w-4 mr-2 flex-shrink-0" />
                    {resident.display_name}
                  </Button>
                ))
              )}

              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-4"
                onClick={onBack}
              >
                <ArrowLeft className="h-4 w-4" />
                Tillbaka
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Enter name */}
        {step === 'name' && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handleSendRequest} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="visitor-name" className="text-sm font-medium">
                    Ditt namn
                  </label>
                  <input
                    id="visitor-name"
                    type="text"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                    placeholder="Förnamn Efternamn"
                    required
                    autoFocus
                    maxLength={100}
                    className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || !visitorName.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Bell className="h-5 w-5" />
                      Skicka förfrågan
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setSelectedResident(null);
                    setStep('residents');
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Välj annan person
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Waiting for response */}
        {step === 'waiting' && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6 text-center space-y-6">
              {/* Pulsating bell animation */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
                  <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 border-2 border-primary/30">
                    <Bell className="h-10 w-10 text-primary animate-bounce" />
                  </div>
                </div>
              </div>

              <div>
                <p className="text-lg font-medium">
                  Förfrågan skickad
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Väntar på svar från {selectedResident?.display_name}
                </p>
              </div>

              {/* Countdown */}
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="font-mono text-sm">{formatTime(timeLeft)}</span>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
              >
                Avbryt
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Approved — show QR key */}
        {step === 'approved' && qrString && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>

              <div>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  Åtkomst beviljad!
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Visa QR-koden för enhetens läsare
                </p>
              </div>

              {/* QR Code display */}
              <div className="bg-white p-4 rounded-xl inline-block mx-auto">
                <QRCodeDisplay value={qrString} size={200} />
              </div>

              {keyExpiresAt && (
                <p className="text-xs text-muted-foreground">
                  Giltig till {new Date(keyExpiresAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step: Denied */}
        {step === 'denied' && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <p className="text-lg font-medium">Åtkomst nekad</p>
              <p className="text-sm text-muted-foreground">
                Personen du sökte avvisade förfrågan
              </p>
              <Button variant="outline" onClick={handleReset}>
                Försök igen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Expired */}
        {step === 'expired' && (
          <Card className="bg-card/80 backdrop-blur border-border animate-fade-in">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
                  <Clock className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <p className="text-lg font-medium">Tiden gick ut</p>
              <p className="text-sm text-muted-foreground">
                Ingen svarade inom tidsfristen
              </p>
              <Button variant="outline" onClick={handleReset}>
                Försök igen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Simple QR code renderer using qrcode.react (already in the project) */
function QRCodeDisplay({ value, size }: { value: string; size: number }) {
  // Lazy import to avoid issues if qrcode.react isn't bundled for this page
  const [QRCode, setQRCode] = useState<any>(null);

  useEffect(() => {
    import('qrcode.react').then((mod) => {
      setQRCode(() => mod.QRCodeSVG);
    });
  }, []);

  if (!QRCode) {
    return (
      <div style={{ width: size, height: size }} className="flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <QRCode value={value} size={size} level="M" />;
}
